import React, { useState, useEffect } from 'react';
import { abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { GameConstants } from '../../../shared/GameConstants';
import { isRangedClass } from '../../../shared/logic/AttackRange';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { CooldownStore } from '../../systems/player/CooldownStore';
import { DebugFCT } from '../../debug/DebugFCT';
import { parseCastFCTEntry } from '../hooks/useCastLifecycleStream';
import { gameLogger } from '../../../utils/GameLogger';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  RADII,
  SPACE,
} from '../theme';
import { Badge, Button } from '../primitives';
import { hasAbilityIcon, abilityIconPath } from '../../rendering/abilities/AbilityIconRegistry';
import { ItemSlot } from '../components/ItemKit';

/**
 * TargetAbilityCheckPanel
 *
 * Live readout of the cast-readiness gates for a selected ability vs
 * the current target. Mirrors the gate stack PlayerManager.
 * performAbility runs (intent → GCD → cooldown → mana → range → execute).
 *
 * Auto-selects the LAST ABILITY CAST so DJ doesn't have to fish through
 * the dropdown — fire fireball, panel switches to fireball; fire
 * mage_auto, panel switches. Override sticks until the next cast.
 */

const gateCardStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 8px',
  background: 'rgba(255, 255, 255, 0.03)',
  border: `1px solid ${COLORS.borderSubtle}`,
  borderRadius: RADII.sm,
};

const gateLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.xs,
  color: COLORS.textPrimary,
  fontSize: FONT_SIZE.xs,
  fontWeight: FONT_WEIGHT.medium,
};

const gateDetailStyle: React.CSSProperties = {
  color: COLORS.textMuted,
  fontFamily: FONTS.mono,
  fontSize: 10,
  marginLeft: SPACE.xs,
};

const selectStyle: React.CSSProperties = {
  flex: 1,
  background: 'rgba(0, 0, 0, 0.45)',
  color: COLORS.textPrimary,
  border: `1px solid ${COLORS.borderSubtle}`,
  borderRadius: RADII.sm,
  padding: '5px 8px',
  fontFamily: FONTS.mono,
  fontSize: FONT_SIZE.xs,
  outline: 'none',
};

/** Live cast-readiness gate snapshot — the shape this panel polls + renders. */
export interface TargetAbilityChecks {
  combatEnable: boolean;
  range: boolean;
  mana: boolean;
  cooldown: boolean;
  gcd: boolean;
  targetName: string;
  distance: number;
  requiredRange: number;
  rangeSource: 'toml' | 'metadata' | 'default-melee' | 'default-ranged' | '';
  manaCost: number;
  currentMana: number;
  cooldownRemainingMs: number;
  gcdRemainingMs: number;
}

/**
 * Serialize the live gate readout to a clipboard-friendly digest.
 */
export function serializeTargetAbilityCheck(abilityLabel: string, c: TargetAbilityChecks): string {
  const mark = (ok: boolean) => (ok ? '✓' : '✗');
  const rangeDetail = c.combatEnable
    ? `${c.distance.toFixed(0)} / ${c.requiredRange}px${c.rangeSource ? ` (${c.rangeSource})` : ''}`
    : `— / ${c.requiredRange}px${c.rangeSource ? ` (${c.rangeSource})` : ''} (no target)`;
  const manaDetail = c.manaCost === 0 ? 'no cost' : `${c.currentMana} / ${c.manaCost}`;
  const cdDetail = c.cooldown ? 'ready' : `${(c.cooldownRemainingMs / 1000).toFixed(1)}s`;
  const gcdDetail = c.gcd ? 'ready' : `${c.gcdRemainingMs}ms`;
  const combatDetail = c.combatEnable ? 'alive' : c.targetName === 'None' ? 'no target' : 'dead';
  return [
    `Target Ability Check — ${abilityLabel}`,
    `Target: ${c.targetName}`,
    `${mark(c.combatEnable)} Combat Enable  ${combatDetail}`,
    `${mark(c.range)} Range          ${rangeDetail}`,
    `${mark(c.mana)} Mana           ${manaDetail}`,
    `${mark(c.cooldown)} Cooldown       ${cdDetail}`,
    `${mark(c.gcd)} GCD            ${gcdDetail}`,
  ].join('\n');
}

export const TargetAbilityCheckPanelContent: React.FC = () => {
  const allAbilities = abilityRegistry.getAll().sort((a, b) => a.id.localeCompare(b.id));
  const [selectedAbilityId, setSelectedAbilityId] = useState<string>('mage_auto');
  const [autoFollowLastCast, setAutoFollowLastCast] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!autoFollowLastCast) return;
    const unsubscribe = DebugFCT.subscribe((entry) => {
      if (entry.channel !== 'cast') return;
      const parsed = parseCastFCTEntry(entry);
      if (!parsed) return;
      setSelectedAbilityId((prev) => (prev === parsed.abilityId ? prev : parsed.abilityId));
    });
    return () => unsubscribe();
  }, [autoFollowLastCast]);

  const [checks, setChecks] = useState<TargetAbilityChecks>({
    combatEnable: false,
    range: false,
    mana: false,
    cooldown: false,
    gcd: false,
    targetName: 'None',
    distance: 0,
    requiredRange: 0,
    rangeSource: '' as 'toml' | 'metadata' | 'default-melee' | 'default-ranged' | '',
    manaCost: 0,
    currentMana: 0,
    cooldownRemainingMs: 0,
    gcdRemainingMs: 0,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const reg = ServiceRegistry.getInstance();
      const targetingSystem = reg.get('TargetingSystem', { optional: true });
      const entitySystem = reg.get('EntitySystem', { optional: true });
      const livePlayer = entitySystem?.getPlayerManager?.()?.getPlayer?.() ?? null;

      const ability = abilityRegistry.getMetadata(selectedAbilityId);
      const tomlAbility = getAbilityData(selectedAbilityId);

      let combatEnable = false;
      let rangeCheck = false;
      let manaCheck = false;
      let cooldownCheck = true;
      let gcdCheck = true;
      let targetName = 'None';
      let distance = 0;
      let requiredRange = 0;
      let rangeSource: 'toml' | 'metadata' | 'default-melee' | 'default-ranged' | '' = '';
      const manaCost = tomlAbility?.manaCost ?? ability?.manaCost ?? ability?.resource?.cost ?? 0;
      const currentMana = (livePlayer as any)?.mana ?? 0;
      let cooldownRemainingMs = 0;
      let gcdRemainingMs = 0;

      if (livePlayer && ability) {
        manaCheck = manaCost === 0 || currentMana >= manaCost;

        const cdExpiration = CooldownStore.getCooldown(selectedAbilityId)
          || (livePlayer as any).cooldowns?.[selectedAbilityId]
          || 0;
        const now = Date.now();
        if (cdExpiration > now) {
          cooldownCheck = false;
          cooldownRemainingMs = cdExpiration - now;
        }

        const lastGCD = CooldownStore.getGCDTime() || (livePlayer as any).lastGCDTime || 0;
        const gcdDuration = (livePlayer as any).gcdOverride !== undefined && (livePlayer as any).gcdOverride >= 0
          ? (livePlayer as any).gcdOverride
          : 500;
        if (gcdDuration > 0 && lastGCD && now - lastGCD < gcdDuration) {
          gcdCheck = false;
          gcdRemainingMs = gcdDuration - (now - lastGCD);
        }

        if (tomlAbility?.range != null) {
          requiredRange = tomlAbility.range;
          rangeSource = 'toml';
        } else if (ability.range != null) {
          requiredRange = ability.range;
          rangeSource = 'metadata';
        } else {
          const isRanged = isRangedClass(livePlayer.class);
          requiredRange = isRanged
            ? GameConstants.Combat.ATTACK_RANGE_RANGED
            : GameConstants.Combat.ATTACK_RANGE_MELEE;
          rangeSource = isRanged ? 'default-ranged' : 'default-melee';
        }

        const targetInfo = targetingSystem?.getTargetInfo() ?? null;
        if (targetInfo && targetInfo.health > 0) {
          combatEnable = true;
          targetName = targetInfo.name || targetInfo.id;
          distance = targetInfo.distance ?? 0;
          rangeCheck = distance <= requiredRange;
        }
      }

      setChecks({
        combatEnable,
        range: rangeCheck,
        mana: manaCheck,
        cooldown: cooldownCheck,
        gcd: gcdCheck,
        targetName,
        distance,
        requiredRange,
        rangeSource,
        manaCost,
        currentMana,
        cooldownRemainingMs,
        gcdRemainingMs,
      });
    }, 100);

    return () => clearInterval(interval);
  }, [selectedAbilityId]);

  const copyReport = () => {
    const selected = allAbilities.find((a) => a.id === selectedAbilityId);
    const abilityLabel = selected ? `${selected.name || selected.id} · ${selected.id}` : selectedAbilityId;
    const text = serializeTargetAbilityCheck(abilityLabel, checks);
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      (err) => gameLogger.warn('ui', `[TargetAbilityCheckPanel] clipboard write failed: ${String(err)}`),
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm, padding: SPACE.sm }}>
      {/* Header with target name and copy button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
          <span style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>TARGET:</span>
          <strong style={{ color: checks.targetName === 'None' ? COLORS.textMuted : COLORS.textPrimary, fontSize: FONT_SIZE.sm }}>
            {checks.targetName}
          </strong>
        </div>
        <Button
          variant="secondary"
          size="xs"
          onClick={copyReport}
          title="Copy the current gate readout to clipboard"
        >
          {copied ? '✓ Copied' : '📋 Copy'}
        </Button>
      </div>

      {/* Ability selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
        {hasAbilityIcon(selectedAbilityId) && (
          <ItemSlot
            item={{
              id: selectedAbilityId,
              name: selectedAbilityId,
              icon: abilityIconPath(selectedAbilityId),
            }}
            size="xs"
          />
        )}
        <select
          style={selectStyle}
          value={selectedAbilityId}
          onChange={(e) => {
            setSelectedAbilityId(e.target.value);
            setAutoFollowLastCast(false);
          }}
        >
          {allAbilities.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name || a.id} ({a.id})
            </option>
          ))}
        </select>
      </div>

      <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 11, color: COLORS.textMuted }}>
        <input
          type="checkbox"
          checked={autoFollowLastCast}
          onChange={(e) => setAutoFollowLastCast(e.target.checked)}
        />
        <span>Auto-follow last cast</span>
      </label>

      {/* 5 Gate Readout Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Combat Enable */}
        <div style={gateCardStyle}>
          <div style={gateLabelStyle}>
            <span>⚔️</span>
            <span>Combat Enable</span>
          </div>
          <Badge tone={checks.combatEnable ? 'success' : 'danger'} size="sm">
            {checks.combatEnable ? '✓ ALIVE' : `✗ ${checks.targetName === 'None' ? 'NO TARGET' : 'DEAD'}`}
          </Badge>
        </div>

        {/* Range */}
        <div style={gateCardStyle}>
          <div style={gateLabelStyle}>
            <span>🎯</span>
            <span>Range</span>
            <span style={gateDetailStyle}>
              {checks.combatEnable && `${checks.distance.toFixed(0)} / `}
              {checks.requiredRange}px
              {checks.rangeSource && ` (${checks.rangeSource})`}
            </span>
          </div>
          <Badge
            tone={!checks.combatEnable ? 'neutral' : checks.range ? 'success' : 'danger'}
            size="sm"
          >
            {!checks.combatEnable ? '—' : checks.range ? '✓ IN RANGE' : '✗ OUT OF RANGE'}
          </Badge>
        </div>

        {/* Mana */}
        <div style={gateCardStyle}>
          <div style={gateLabelStyle}>
            <span>💧</span>
            <span>Mana</span>
            <span style={gateDetailStyle}>
              {checks.manaCost === 0 ? 'no cost' : `${checks.currentMana} / ${checks.manaCost}`}
            </span>
          </div>
          <Badge tone={checks.mana ? 'success' : 'danger'} size="sm">
            {checks.mana ? '✓ SUFFICIENT' : '✗ INSUFFICIENT'}
          </Badge>
        </div>

        {/* Cooldown */}
        <div style={gateCardStyle}>
          <div style={gateLabelStyle}>
            <span>⏱️</span>
            <span>Cooldown</span>
            {!checks.cooldown && (
              <span style={gateDetailStyle}>{(checks.cooldownRemainingMs / 1000).toFixed(1)}s</span>
            )}
          </div>
          <Badge tone={checks.cooldown ? 'success' : 'warning'} size="sm">
            {checks.cooldown ? '✓ READY' : '⏳ ACTIVE'}
          </Badge>
        </div>

        {/* GCD */}
        <div style={gateCardStyle}>
          <div style={gateLabelStyle}>
            <span>⚡</span>
            <span>GCD</span>
            {!checks.gcd && (
              <span style={gateDetailStyle}>{checks.gcdRemainingMs}ms</span>
            )}
          </div>
          <Badge tone={checks.gcd ? 'success' : 'warning'} size="sm">
            {checks.gcd ? '✓ READY' : '⏳ ACTIVE'}
          </Badge>
        </div>
      </div>
    </div>
  );
};
